import React, { useEffect, useState } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport,Search, Edit, Inject } from '@syncfusion/ej2-react-grids';
import Swal from 'sweetalert2';
import { Header } from '../components';
import { MdDeleteForever , MdEditNote  } from "react-icons/md";

const Funds = () => {
  const [fundsData, setFundsData] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEditFund, setCurrentEditFund] = useState(null);
  const [newFund, setNewFund] = useState({
    fname: '',
    amount: '',
    tid: '',
    type: '',
    date: '',
    _id: null,
  });
  
  useEffect(() => {
    fetchFunds();
  }, []);
  
  const fetchFunds = async () => {
    try {
      if (!process.env.REACT_APP_API_BASE_URL || !process.env.REACT_APP_API_KEY) {
        throw new Error('API configuration missing');
      }
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/funds', {
        headers: {
          'api-key': process.env.REACT_APP_API_KEY,
        },
      });
  
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
  
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format received');
      }
  
      // Sort data by date in descending order (latest first)
      const sortedData = [...data].sort((a, b) => new Date(b.date) - new Date(a.date));
      setFundsData(sortedData);
    } catch (error) {
      console.error('Error fetching funds:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch funds',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };
   
  const handleEditFund = (fund) => {
    setIsEditMode(true);
    setShowAddForm(true);
    const formattedDate = new Date(fund.date).toISOString().split('T')[0];
    setNewFund({
      fname: fund.fname,
      amount: fund.amount,
      tid: fund.tid,
      type: fund.type,
      date: formattedDate,
      _id: fund._id,
    });
    setCurrentEditFund(fund);
  };
  
  const handleAddFund = async (e) => {
    e.preventDefault();
  
    try {
      if (
        !newFund.fname?.trim() ||
        !newFund.date?.trim() ||
        !newFund.tid?.trim() ||
        !newFund.type?.trim() ||
        !newFund.amount
      ) {
        await Swal.fire({
          title: 'Error!',
          text: 'All fields are required',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d33',
        });
        return;
      }
        // Additional validation for amount (number type)
    if (isNaN(newFund.amount) || newFund.amount <= 0) {
      await Swal.fire({
        title: 'Error!',
        text: 'Amount must be a valid positive number',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
      return;
    }

  
      if (!process.env.REACT_APP_API_BASE_URL || !process.env.REACT_APP_API_KEY) {
        throw new Error('API configuration is missing');
      }
  
      const formattedDate = new Date(newFund.date).toISOString().split('T')[0];
      const fundData = {
        fname: newFund.fname,
        amount: Number(newFund.amount),
        tid: newFund.tid,
        type: newFund.type,
        date: formattedDate,
      };
  
      const method = isEditMode ? 'PUT' : 'POST';
      const url = isEditMode
        ? `${process.env.REACT_APP_API_BASE_URL}/api/funds/${newFund._id}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/funds`;
  
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'api-key': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(fundData),
      });
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to add/update fund');
      }
  
      await Swal.fire({
        title: 'Success!',
        text: isEditMode ? 'Fund updated successfully' : 'Fund added successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
  
      setShowAddForm(false);
      setIsEditMode(false);
      setNewFund({ fname: '', amount: '', tid: '', type: 'credit', date: '', _id: null });
      await fetchFunds();
    } catch (error) {
      console.error('Error adding/updating fund:', error);
      await Swal.fire({
        title: 'Error!',
        text: error.message || 'An unexpected error occurred',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
    }
  };
  


  const handleDelete = async (id) => {
    try {
      if (!process.env.REACT_APP_API_BASE_URL || !process.env.REACT_APP_API_KEY) {
        throw new Error("API configuration missing");
      }
  
      // Prompt for password
      const { value: password } = await Swal.fire({
        title: "Enter Password",
        input: "password",
        inputLabel: "Password is required to delete the fund.",
        inputPlaceholder: "Enter your password",
        showCancelButton: true,
        confirmButtonText: "Submit",
        confirmButtonColor: "#d33", // Red button for submit
        cancelButtonText: "Cancel",
        cancelButtonColor: "#3085d6", // Blue button for cancel
        inputValidator: (value) => {
          if (!value) {
            return "You need to enter a password!";
          }
        },
      });
  
      // Check password
      if (!password) {
        // User canceled the dialog
        return;
      }
  
      if (password !== "8828") {
        await Swal.fire({
          icon: "error",
          title: "Incorrect Password",
          text: "The password you entered is incorrect.",
          confirmButtonText: "Okay",
          confirmButtonColor: "#4caf50", // Green button
        });
        return; // Stop further execution if the password is incorrect
      }
  
      // Perform delete operation if password is correct
      const fundResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/funds/${id}`,
        {
          method: "DELETE",
          headers: {
            "api-key": process.env.REACT_APP_API_KEY,
          },
        }
      );
  
      if (!fundResponse.ok) {
        throw new Error("Failed to delete fund");
      }
  
      // Refresh funds after deletion
      await fetchFunds();
  
      await Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "The fund has been deleted successfully.",
        confirmButtonText: "Okay",
        confirmButtonColor: "#4caf50", // Green button
      });
  
      return true;
    } catch (error) {
      console.error("Error deleting fund:", error);
  
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while deleting the fund.",
        confirmButtonText: "Okay",
        confirmButtonColor: "#4caf50", // Green button
      });
  
      throw error;
    }
  };
  
  
  

  const fundsGrid = [
  
    {
      field: 'fname',
      headerText: 'Name',
      width: '150',
      textAlign: 'Center',
    },
    {
      field: 'amount',
      headerText: 'Amount',
      width: '150',
      textAlign: 'Center',
    },
    {
      field: 'tid',
      headerText: 'T_id',
      width: '150',
      textAlign: 'Center',
    },
    {
      field: 'type',
      headerText: 'Type',
      textAlign: 'Center',
      width: '150',
      template: (props) => (
        <div>
          {props.type && (
            <button
              type="button"
              className={`${
                props.type.toLowerCase() === 'credit'
                  ? 'bg-green-300'
                  : 'bg-red-400'
              } text-white py-1 px-1 capitalize rounded-md text-md`}
            >
              {props.type}
            </button>
          )}
        </div>
      )
    },
    
    {
      field: 'date',
      headerText: 'Date',
      width: '120',
      textAlign: 'Center',
    },
    {
      field: 'updatedAt',
      headerText: 'Updated At',
      width: '120',
      textAlign: 'Center',
    },
    {
      field: '_id',
      headerText: 'Actions',
      width: '150',
      textAlign: 'Center',
      template: (props) => (
        <div>
          <button 
            className="bg-blue-500 text-white px-1 py-1 text-xl rounded mr-2"
            onClick={() => handleEditFund(props)}
          >
            <MdEditNote />
          </button>
          <button
            className="bg-red-500 text-white px-1 py-1 text-xl rounded"
            onClick={() => {
              Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!',
              }).then(async (result) => {
                if (result.isConfirmed) {
                  try {
                    await handleDelete(props._id);

                    Swal.fire({
                      title: 'Deleted!',
                      text: 'Fund has been deleted.',
                      icon: 'success',
                      confirmButtonText: 'OK',
                      confirmButtonColor: '#3085d6',
                    });
                  } catch (error) {
                    Swal.fire({
                      title: 'Error!',
                      text: 'Failed to delete the Fund or associated file.',
                      icon: 'error',
                      confirmButtonText: 'OK',
                      confirmButtonColor: '#3085d6',
                    });
                  }
                }
              });
            }}
          >
          <MdDeleteForever/>
          </button>
        </div>
      )
    },
  ];

  return (
    <div className="m-2 md:m-10 sm:mt-24 p-2 md:p-4 bg-white rounded-3xl">
      <div className="flex justify-between items-center mb-4">
        <Header category="Page" title="Funds" />
        <button
          onClick={() => {
            setShowAddForm(true);
            setIsEditMode(false);
            setNewFund({ fname: '', amount: '', tid: '', type: '', date: '' });
          }}
          className="bg-green-500 text-white px-3 py-2 rounded"
        >
          Add Fund
        </button>
      </div>

      {showAddForm && (
       <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex justify-center items-center">
       <div className="bg-white p-8 rounded-xl shadow-2xl w-[600px]">
         <h2 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-2">
           {isEditMode ? 'Edit Fund' : 'Add New Fund'}
         </h2>
         <form onSubmit={handleAddFund} className="space-y-6">
           {/* Name Field */}
           <div className="mb-4">
             <label className="block text-gray-700 text-sm font-bold mb-2">Name:</label>
             <input
               type="text"
               value={newFund.fname}
               onChange={(e) => setNewFund({ ...newFund, fname: e.target.value })}
               className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
               required
               placeholder="Enter name"
             />
           </div>
     
           {/* Amount Field */}
           <div className="mb-4">
             <label className="block text-gray-700 text-sm font-bold mb-2">Amount:</label>
             <input
               type="number"
               value={newFund.amount}
               onChange={(e) => setNewFund({ ...newFund, amount: e.target.value })}
               className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
               required
               placeholder="Enter amount"
             />
           </div>
     
           {/* Fund ID Field */}
           <div className="mb-4">
             <label className="block text-gray-700 text-sm font-bold mb-2">Transaction ID:</label>
             <input
               type="text"
               value={newFund.tid}
               onChange={(e) => setNewFund({ ...newFund, tid: e.target.value })}
               className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
               required
               placeholder="Enter fund ID"
             />
           </div>
     
           {/* Type Field */}
           <div className="mb-4">
             <label className="block text-gray-700 text-sm font-bold mb-2">Type:</label>
             <div className="flex space-x-4">
               <label className="flex items-center space-x-2">
                 <input
                   type="radio"
                   value="credit"
                   checked={newFund.type === 'credit'}
                   onChange={(e) => setNewFund({ ...newFund, type: e.target.value })}
                   className="focus:ring-blue-500"
                 />
                 <span>Credit</span>
               </label>
               <label className="flex items-center space-x-2">
                 <input
                   type="radio"
                   value="debit"
                   checked={newFund.type === 'debit'}
                   onChange={(e) => setNewFund({ ...newFund, type: e.target.value })}
                   className="focus:ring-blue-500"
                 />
                 <span>Debit</span>
               </label>
             </div>
           </div>
     
           {/* Date Field */}
           <div className="mb-4">
             <label className="block text-gray-700 text-sm font-bold mb-2">Date:</label>
             <input
               type="date"
               value={newFund.date}
               onChange={(e) => setNewFund({ ...newFund, date: e.target.value })}
               className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
               required
             />
           </div>
     
           {/* Form Buttons */}
           <div className="flex justify-end space-x-4 pt-4 border-t">
             <button
               type="button"
               onClick={() => {
                 setShowAddForm(false);
                 setIsEditMode(false);
                 setNewFund({ name: '', amount: '', tid: '', type: '', date: '' });
               }}
               className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg transition duration-200 font-medium"
             >
               Cancel
             </button>
             <button
               type="submit"
               className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg transition duration-200 font-medium"
             >
               {isEditMode ? 'Update Fund' : 'Add Fund'}
             </button>
           </div>
         </form>
       </div>
     </div>
     
      )}

      <GridComponent
        id="gridcomp"
        dataSource={fundsData}
        allowPaging
        allowSorting
        toolbar={['Search']}
        width="auto"
      >
        <ColumnsDirective>
          {fundsGrid.map((item, index) => (
            <ColumnDirective key={index} {...item} />
          ))}
        </ColumnsDirective>
        <Inject services={[Resize, Search, Sort, ContextMenu, Filter, Page, ExcelExport, Edit, PdfExport]} />
      </GridComponent>
    </div>
  );
};

export default Funds;